import {
    Communication,
    Customer,
    Invoice,
    ContactChange,
    Pagination,
    PaymentFormData,
    Competitor,
    NextInvoice,
    DeliveryFlowOverview,
    DefaultDeliveryFlow,
    CommunicationChannel, Hardware, CommunicationRequestId
} from "./types";
import {redirect} from "react-router-dom";
import Routing from "../Routing.ts";

const BASE_API_URI = '/v4/external'

export interface Response {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any
    code: number
}

const postJson = async (uri: string, body?: object): Promise<Response> => {
    const response = await fetch(uri, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: "include",
    })

    return {
        response: await response.json(),
        code: response.status
    }
}

export const getJson = async (uri: string): Promise<Response> => {
    const response = await fetch(uri, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: "include",
    })


    return {
        response: await response.json().then(authCheck),
        code: response.status
    }
}

const authCheck = (value: Response): Response | PromiseLike<Response> => {
    if (value.code === 401) {
        redirect(Routing.Selfcare.Login)
    }
    return value;
};

interface SelfCareLoginResponse extends Response {
    response: {
        error_message?: string
        success_message?: string
        customer_ids?: Array<string>
    }
}

interface SelfCareLoginCheckResponse extends Response {
    response: {
        logged_in: boolean
    }
}

export const selfcareLoginCheck = async (): Promise<SelfCareLoginCheckResponse> => {
    return await getJson(`/mit-hiper/api/login/tjek`)
}

export const selfcareLogin = async (email: string, password: string, remember_me: boolean): Promise<SelfCareLoginResponse> => {
    return await postJson(`/mit-hiper/api/login`, {email, password, remember_me})
}

export const selfcareLogout = async (): Promise<Response> => {
    return await postJson(`/mit-hiper/api/logout`)
}

export const selfcareLoginAdd = async (email: string): Promise<SelfCareLoginResponse> => {
    return await postJson(`/mit-hiper/api/login/tilfoej`, {email})
}

export const selfcareCreatePassword = async (passwordOne: string, passwordTwo: string, hash: string, remember_me: boolean): Promise<SelfCareLoginResponse> => {
    return await postJson(`/mit-hiper/api/login/kode/gem`, {
        'password_one': passwordOne,
        'password_two': passwordTwo,
        'hash': hash,
        'remember_me': remember_me
    })
}

interface SelfcareRelatedCustomersResponse extends Response {
    response: {
        status: string
        data: Customer[]
    }
}

export const selfcareRelatedCustomers = async (): Promise<SelfcareRelatedCustomersResponse> => {
    return await getJson(`${BASE_API_URI}/customer/related`)
}


interface SelfcareInvoicesResponse extends Response {
    response: {
        status: string
        data: Invoice[]
        pagination: Pagination
    }
}

interface SelfcareCommunicationsResponse extends Response {
    response: {
        status: string
        data: Communication[]
        pagination: Pagination
    }
}

interface SelfcareCommunicationResponse extends Response {
    response: {
        status: string
        data: Communication
    }
}

interface SelfcareHardwareResponse extends Response {
    response: {
        status: string
        data: Hardware[]
    }
}

export const selfcareInvoices = async (customer_id: string, page: number): Promise<SelfcareInvoicesResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/invoice?page=${page}`)
}
export const selfcareCommunications = async (customer_id: string, page: number, per_page?: number, workflow_instance_id?: undefined|number): Promise<SelfcareCommunicationsResponse> => {
    let url = `${BASE_API_URI}/customer/${customer_id}/communication/request`
    url += `?exclude=${encodeURIComponent('body,attachments')}`
    url += `&channels=${encodeURIComponent(`${CommunicationChannel.EMAIL},${CommunicationChannel.SMS}`)}`
    url += `&page=${encodeURIComponent(page.toString())}`

    if (per_page) {
        url += `&per_page=${encodeURIComponent(per_page.toString())}`
    }

    if (workflow_instance_id) {
        url += `&workflow_instance_id=${encodeURIComponent(workflow_instance_id.toString())}`
        url += `&channels=${encodeURIComponent(CommunicationChannel.NOTIFICATION)}`
    }

    return await getJson(url)
}

export const selfcareCommunication = async (customer_id: string, request_id: CommunicationRequestId): Promise<SelfcareCommunicationResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/communication/request/${request_id}`)
}

export const patchSelfcareCommunication = async (customer_id: string, request_id: CommunicationRequestId, body: object): Promise<SelfcareCommunicationResponse> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/communication/request/${request_id}/patch`, body)
}

export const selfcareHardware = async (customer_id: string): Promise<SelfcareHardwareResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/hardware`)
}

interface SelfcareCustomerResponse extends Response {
    response: {
        status: string,
        data: Customer
    }
}

export const selfcareCustomer = async (customer_id: string): Promise<SelfcareCustomerResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}`)
}

export const selfcareChangePassword = async (password: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/change-password`, {password})
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selfcareChangeContactPermissions = async (customer_id: string, permissions: any[] ): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/permission/save`, {permissions})
}

export const selfcareContactPermissions = async (customer_id: string): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/permission`)
}

export const selfcareCustomerAcceptedPermissionText = async (customer_id: string): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/permission-text-accepted`)
}

export const selfcareLatestPermissionText = async (customer_id: string): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/permission_text`)
}

export const selfcareCheckPopupEligible = async (customer_id: string): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/check-popup-eligible`)
}

export const selfcareSetPopupIneligible = async (customer_id: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/set-popup-ineligible`)
}

export const selfcareChangeMobile = async (customer_id: string, mobile: number): Promise<SelfcareCustomerContactChangeResponse> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/change-mobile`, {mobile})
}

export const selfcareChangeMobileConfirm = async (customer_id: string, secret: string): Promise<SelfcareCustomerContactChangeResponse> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/change-mobile-confirm`, {secret})
}

export const selfcareChangeMobileCancel = async (customer_id: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/change-mobile-cancel`, {})
}

interface SelfcareCustomerContactChangeResponse extends Response {
    response: {
        status: string,
        data: ContactChange
    }
}

export const selfcareChangeEmail = async (customer_id: string, email: string): Promise<SelfcareCustomerContactChangeResponse> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/change-email`, {email})
}

export const selfcareChangeEmailCancel = async (customer_id: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/change-email-cancel`, {})
}

interface SelfcareCustomerPaymentChangeFormDataResponse extends Response {
    response: {
        status: string,
        data: PaymentFormData
    }
}

export const selfcareChangePaymentFormData = async (customer_id: string): Promise<SelfcareCustomerPaymentChangeFormDataResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/change-payment/form-data`)
}

export const selfcareChangePayment = async (customer_id: string, quickpay_order_id: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/change-payment`, {quickpay_order_id})
}

export const selfcarePayOverdueInvoices = async (customer_id: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/invoice/pay-overdue-invoices`)
}

interface SelfcarePaymentStatusResponse extends Response {
    response: {
        status: string,
        data: {
            is_pay_overdue_invoices_active: boolean
        }
    }
}

export const selfcarePaymentStatus = async (customer_id: string): Promise<SelfcarePaymentStatusResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/invoice/payment-status`)
}

interface SelfcareNextInvoicesResponse extends Response {
    response: {
        status: string
        data: NextInvoice
    }
}

export const selfcareNextInvoice = async (customer_id: string): Promise<SelfcareNextInvoicesResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/invoice/next-invoice`)
}

export const selfcareRegisterSbbu = async (customer_id: string, formData: object): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/register-sbbu`, formData)
}

interface CompetitorResponse extends Response {
    response: {
        status: string,
        data: Competitor[]
    }
}

export const competitor = async (): Promise<CompetitorResponse> => {
    return await getJson(`${BASE_API_URI}/competitor`)
}

interface DeliveryFlowOverviewResponse extends Response {
    response: {
        status: string,
        data: DeliveryFlowOverview[]
    }
}

interface DeliveryFlowResponse extends Response {
    response: {
        status: string,
        data: DefaultDeliveryFlow
    }
}

export const getDeliveryFlowOverview = async (customer_id: string): Promise<DeliveryFlowOverviewResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/timeline`)
}

export const getDeliveryFlow = async (customer_id: string, workflow_instance_id: number): Promise<DeliveryFlowResponse> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/timeline/${workflow_instance_id}`)
}

export const enableMpro = async (customer_id: string, serial: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/mpro/enable`, {serial})
}

export const disableMpro = async (customer_id: string, serial: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/mpro/disable`, {serial})
}

export const statusMpro = async (customer_id: string, serial: string): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/mpro/status/${serial}`)
}

export const selfcareReportOffline = async (customer_id: string, offline_message: string, contact_phone: boolean, contact_email: boolean): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/report-offline`, {offline_message, contact_phone, contact_email})
}

export const contactForm = async (
    category_id: number,
    street_name: string,
    street_number: string,
    street_floor: string,
    street_side: string,
    street_zip: string,
    name: string,
    email: string,
    mobile: string,
    comment: string,
): Promise<Response> => {
    return await postJson(`/kontakt/gem-besked`, {
        category_id,
        street_name,
        street_number,
        street_floor,
        street_side,
        street_zip,
        name,
        email,
        mobile,
        comment,
    })
}

export const saveRmo = async (name: string, number: string): Promise<Response> => {
    return await postJson(`/call-me/save`, {name, number})
}

export interface ProductListPrice {
    id: number,
    line_speed_down: number,
    line_speed_up: number,
    price: number
}

export interface ProductListPriceProviders {
    tdc: ProductListPrice[]
    tm: ProductListPrice[]
    sef: ProductListPrice[]
    rah: ProductListPrice[]
    n: ProductListPrice[]
    ne: ProductListPrice[]
    gc: ProductListPrice[]
    f: ProductListPrice[]
    e: ProductListPrice[]
    ef: ProductListPrice[]
    mbb: ProductListPrice[]
    coax: ProductListPrice[]
}

export interface AdjustmentsListPrices {
  initiation: AdjustmentsListProductSpecific
  technician: AdjustmentsListProductSpecific
  vain_technician: number
  moving: AdjustmentsListProductSpecific
  shipping: number
  soft_open: number
  router: AdjustmentsListProductSpecific
  fiber_gpon: number
  reminder_fee_invoice: number
  reminder_fee_hardware: number
  payment_card: number
  new_product: AdjustmentsListProductSpecific
  static_ip: number
  dyn_ip: number
  extra_digging_meter: number
  fiberbox_inside: number
  cable_inside: number
}

export interface AdjustmentsListProductSpecific {
  fiber?: number
  coax?: number
  mbb?: number
}

interface PriceListResponse extends Response {
    response: {
        products: ProductListPriceProviders,
        adjustments: AdjustmentsListPrices
    }
}

export const getPriceList = async (): Promise<PriceListResponse> => {
    return await getJson(`/bredbaand/pricelist`)
}

export const getCpeStatus = async (customer_id: string, product_instance_id: number): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/product-instance/${product_instance_id}/cpe`)
}

export const getOntStatus = async (customer_id: string, product_instance_id: number): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/product-instance/${product_instance_id}/ont`)
}

export const getWifiStatus = async (customer_id: string, product_instance_id: number): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/product-instance/${product_instance_id}/wifi`)
}

export const getOptIn = async (customer_id: string, type: string): Promise<Response> => {
    return await getJson(`${BASE_API_URI}/customer/${customer_id}/opt-in/${type}`)
}

export const createOptIn = async (customer_id: string, type: string, method: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/opt-in`, {type, method})
}

export const confirmOptIn = async (customer_id: string, opt_in_id: number, verification_code: string): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/opt-in-confirm`, {opt_in_id, verification_code})
}

export const updateWifi = async (customer_id: string, product_instance_id: number, body: object): Promise<Response> => {
    return await postJson(`${BASE_API_URI}/customer/${customer_id}/product-instance/${product_instance_id}/update-wifi`, body)
}
